import { Button } from '@synoptic/ui-kit/button/button.js';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import { ArrowLeftIcon } from '@synoptic/ui-kit/icons/react/arrow-left.js';
import { ArrowRightIcon } from '@synoptic/ui-kit/icons/react/arrow-right.js';
import { CheckIcon } from '@synoptic/ui-kit/icons/react/check.js';
import { CircleSparkIcon } from '@synoptic/ui-kit/icons/react/circle-spark.js';
import { PinIcon } from '@synoptic/ui-kit/icons/react/pin.js';
import { SendIcon } from '@synoptic/ui-kit/icons/react/send.js';
import {
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@synoptic/ui-kit/modal/modal.js';
import { ModalRoot, ModalTrigger } from '@synoptic/ui-kit/modal/modal.js';
import { Textarea } from '@synoptic/ui-kit/textarea/textarea.js';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  AiFilterAssistantResponseType,
  AiFilterSuggestion,
} from '@/__generated__/schema-types';
import { useAiAssistant, AiAssistantHistoryItem } from '../use-ai-assistant';
import { backButton } from '../ai-filters.css';
import {
  textareaInput,
  textareaFooter,
  userMessage,
  assistantMessageContainer,
  chatContainer,
  assistantMessageList,
  assistantMessageContent,
  useThisFilterButton,
  assistantLoadingMessage,
  createWithAIAssistantCard,
  createWithAIAssistantCardHeader,
  createWithAIAssistantCardDescription,
  assistantModalContent,
  createWithAIAssistantContainer,
  createWithAIAssistantPlaceholder,
  createWithAIAssistantPlaceholderTitle,
  createWithAIAssistantPlaceholderDescription,
  passValue,
  assistantFilterSuggestionSelected,
  assistantFilterSuggestionSelectedLabel,
  manageFilterModal,
  assistantModalFooter,
  assistantModalHeader,
  assistantTextareaContainer,
  footerButtonsContainer,
  goToSelectedFilter,
  goToSelectedFilterTitle,
  goToSelectedFilterDescription,
  goToSelectedFilterBelowViewport,
  chatContainerWrapper,
} from './manage-filter.css';

interface CreateWithAIAssistantModalProps {
  onSelectFilterSuggestion: (filter: AiFilterSuggestion) => void;
}

export const CreateWithAIAssistantModal = ({
  onSelectFilterSuggestion,
}: CreateWithAIAssistantModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] =
    useState<AiFilterSuggestion | null>(null);

  const [message, setMessage] = useState('');
  const { aiAssistantHistory, getSuggestion, suggestion, loading } =
    useAiAssistant();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { ref, inView, entry } = useInView({
    skip: !selectedFilter,
    initialInView: true,
  });

  const isBelowViewport =
    !!entry?.boundingClientRect.top && entry.boundingClientRect.top > 0;

  return (
    <ModalRoot open={isOpen} onOpenChange={setIsOpen}>
      <ModalTrigger asChild>
        <div className={createWithAIAssistantCard}>
          <div>
            <header className={createWithAIAssistantCardHeader}>
              <CircleSparkIcon width={16} height={16} />
              <p>Let AI Assistant help you set up your filter</p>
            </header>
            <p className={createWithAIAssistantCardDescription}>
              Not sure what to enter? Describe what you need in chat, pick the
              best option, and AI will pre-fill the fields for you.
            </p>
          </div>
          <ArrowRightIcon width={24} height={24} />
        </div>
      </ModalTrigger>
      <ModalContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef?.current?.focus();
        }}
        className={manageFilterModal}
        contentClassName={assistantModalContent}
      >
        <ModalHeader className={assistantModalHeader} withoutCloseIcon>
          <IconButton
            onClick={() => setIsOpen(false)}
            className={backButton}
            variant="ghost"
            size="small"
          >
            <ArrowLeftIcon style={{ width: 24, height: 24 }} />
          </IconButton>
          <ModalTitle>Create with AI assistant</ModalTitle>
        </ModalHeader>
        <div className={createWithAIAssistantContainer}>
          {aiAssistantHistory.length === 0 ? (
            <div className={createWithAIAssistantPlaceholder}>
              <CircleSparkIcon width={34} height={34} />
              <p className={createWithAIAssistantPlaceholderTitle}>
                I&apos;ll help you build your filter
              </p>
              <p className={createWithAIAssistantPlaceholderDescription}>
                Tell AI what kind of content you want to filter, and it will
                generate suggestions. Pick the one that fits best, and
                we&apos;ll fill in the details for you.
              </p>
            </div>
          ) : (
            <div className={chatContainerWrapper}>
              {!inView && selectedFilter && (
                <div
                  className={clsx(goToSelectedFilter, {
                    [goToSelectedFilterBelowViewport]: isBelowViewport,
                  })}
                  onClick={() => {
                    const selectedElement = document.querySelector(
                      `.${assistantFilterSuggestionSelected}`,
                    );
                    if (selectedElement) {
                      selectedElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    }
                  }}
                >
                  <div>
                    <p className={goToSelectedFilterTitle}>Selected filter</p>
                    <p className={goToSelectedFilterDescription}>
                      Here’s a filter setup based on your request
                    </p>
                  </div>
                  <PinIcon width={16} height={16} />
                </div>
              )}
              <ChatWithAIAssistant
                onSelectSuggestion={(suggestion) => {
                  setSelectedFilter(suggestion);
                }}
                aiAssistantHistory={aiAssistantHistory}
                preparingAnswer={loading}
                selectedFilter={selectedFilter}
                ref={ref}
              />
            </div>
          )}
          <div
            onClick={() => textareaRef.current?.focus()}
            className={assistantTextareaContainer}
          >
            <Textarea
              autoResize
              autoFocus
              rows={1}
              ref={textareaRef}
              placeholder="Describe the filter you want to create"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              containerClassName={textareaInput}
            />
            <div className={textareaFooter}>
              <IconButton
                size="small"
                disabled={loading || message.length === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setMessage('');
                  getSuggestion({
                    prompt: message,
                    chatId: suggestion?.chatId ?? undefined,
                  });
                }}
              >
                <SendIcon />
              </IconButton>
            </div>
          </div>
        </div>
        <div className={assistantModalFooter}>
          <div className={footerButtonsContainer}>
            <Button onClick={() => setIsOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedFilter) {
                  onSelectFilterSuggestion(selectedFilter);
                  setIsOpen(false);
                }
              }}
              disabled={!selectedFilter}
              type="submit"
              variant="primary"
            >
              Apply
            </Button>
          </div>
        </div>
      </ModalContent>
    </ModalRoot>
  );
};

interface ChatWithAIAssistantProps {
  aiAssistantHistory: AiAssistantHistoryItem[];
  onSelectSuggestion: (suggestion: AiFilterSuggestion) => void;
  preparingAnswer: boolean;
  selectedFilter?: AiFilterSuggestion | null;
}

const ChatWithAIAssistant = ({
  ref,
  aiAssistantHistory,
  onSelectSuggestion,
  preparingAnswer,
  selectedFilter,
}: ChatWithAIAssistantProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div className={chatContainer}>
      {aiAssistantHistory.map((item, index) => {
        const isSelectedFilter = selectedFilter === item;
        if ('isUserMessage' in item) {
          return (
            <div key={index} className={userMessage}>
              {item.prompt}
            </div>
          );
        }
        const isFilterCorrect =
          item.responseType === AiFilterAssistantResponseType.Complete;

        const filterRefProp = isSelectedFilter ? { ref } : {};

        return (
          <div
            key={index}
            className={clsx(assistantMessageContainer, {
              [assistantFilterSuggestionSelected]: isSelectedFilter,
            })}
            {...filterRefProp}
          >
            <div className={assistantMessageContent}>
              {isFilterCorrect ? (
                <>
                  {isSelectedFilter && (
                    <p className={assistantFilterSuggestionSelectedLabel}>
                      <CheckIcon width={16} height={16} /> Selected filter
                    </p>
                  )}
                  <p>Here&apos;s a filter setup based on your request:</p>
                  <ul className={assistantMessageList}>
                    <li>Your Prompt: {item.prompt}</li>
                    <li>
                      {'Pass filter when prompt output is: '}
                      <span className={passValue}>
                        {item.passValue[0].toLowerCase()}
                      </span>
                    </li>
                  </ul>
                  <p>Let me know if you need any adjustments!</p>
                </>
              ) : (
                <span>{item.prompt}</span>
              )}
            </div>
            {isFilterCorrect && !isSelectedFilter && (
              <Button
                onClick={() => {
                  onSelectSuggestion(item);
                }}
                variant="outline"
                size="small"
                className={useThisFilterButton}
              >
                Choose this filter
              </Button>
            )}
          </div>
        );
      })}
      {preparingAnswer && (
        <p className={assistantLoadingMessage}>
          <CircleSparkIcon width={16} height={16} />
          Preparing answer
        </p>
      )}
    </div>
  );
};

ChatWithAIAssistant.displayName = 'ChatWithAIAssistant';
